<!-- 
	This is the transactions page, it uses the dashboard layout in: 
	"./layouts/Dashboard.vue" .
 -->

<template>
	<div class="transactions">

		<!-- Header -->
		<a-row :gutter="24" type="flex" class="mb-24">
			<a-col :span="24">
				<a-card :bordered="false" class="header-solid h-full">
					<template #title>
						<h5 class="font-semibold">Transactions</h5>
						<p>View, search, add, edit and delete transactions</p>
					</template>
					<template #extra v-if="canEdit">
						<a-button type="primary" @click="openCreateModal">
							<a-icon type="plus" />
							Add Transaction
						</a-button>
					</template>
				</a-card>
			</a-col>
		</a-row>

		<!-- Filters -->
		<a-row :gutter="24" type="flex" class="mb-24">
			<a-col :span="24">
				<a-card :bordered="false">
					<template #title>
						<a-button type="link" @click="filtersCollapsed = !filtersCollapsed" style="padding: 0; font-weight: 500;">
							<a-icon :type="filtersCollapsed ? 'down' : 'up'" />
							Filters
						</a-button>
					</template>
					<a-form layout="inline" v-show="!filtersCollapsed">
						<a-form-item label="Type">
							<a-select v-model="filters.type" allowClear style="width: 140px" placeholder="Any">
								<a-select-option value="INCOME">INCOME</a-select-option>
								<a-select-option value="EXPENSE">EXPENSE</a-select-option>
							</a-select>
						</a-form-item>
						<a-form-item label="Category">
							<a-select v-model="filters.categoryId" allowClear show-search optionFilterProp="children" style="width: 220px" placeholder="Any">
								<a-select-option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</a-select-option>
							</a-select>
						</a-form-item>
						<a-form-item label="From">
							<a-date-picker v-model="filters.startDate" show-time style="width: 220px" placeholder="Start date" />
						</a-form-item>
						<a-form-item label="To">
							<a-date-picker v-model="filters.endDate" show-time style="width: 220px" placeholder="End date" />
						</a-form-item>
						<a-form-item label="Min">
							<a-input-number v-model="filters.minAmount" :min="0" style="width: 120px" />
						</a-form-item>
						<a-form-item label="Max">
							<a-input-number v-model="filters.maxAmount" :min="0" style="width: 120px" />
						</a-form-item>
						<a-form-item>
							<a-button type="primary" @click="applyFilters">Search</a-button>
						</a-form-item>
						<a-form-item>
							<a-button @click="resetFilters">Reset</a-button>
						</a-form-item>
					</a-form>
				</a-card>
			</a-col>
		</a-row>

		<!-- Transactions Table/Cards -->
		<a-row :gutter="24" type="flex">
			<a-col :span="24">
				<a-card :bordered="false">
					<!-- Desktop Table View -->
					<div class="desktop-table">
						<a-table :data-source="transactions" :loading="loading" :rowKey="getRowKey"
							:pagination="pagination" @change="handleTableChange">
							<a-table-column key="date" title="Date" :width="140">
								<template slot-scope="text, record">
									{{ formatDisplayDate(record.date) }}
								</template>
							</a-table-column>
							<a-table-column key="amount" title="Amount">
								<template slot-scope="text, record">
									{{ (record.type === 'EXPENSE' ? '-' : '+') + record.amount }}
								</template>
							</a-table-column>
							<a-table-column key="type" title="Type" dataIndex="type" />
							<a-table-column key="categoryName" title="Category" dataIndex="categoryName" />
							<a-table-column key="notes" title="Notes" dataIndex="notes" />
							<a-table-column key="createdBy" title="User" dataIndex="createdBy" />
							<a-table-column key="receipt" title="Receipt" :width="80">
								<template slot-scope="text, record">
									<a-button v-if="record.receiptUrl" size="small" type="link" @click="downloadReceipt(record.id, record.receiptUrl)">
										<a-icon type="download" />
									</a-button>
									<span v-else>-</span>
								</template>
							</a-table-column>
							<a-table-column v-if="canEdit" key="actions" title="Actions" :width="160">
								<template slot-scope="text, record">
									<div>
										<a-button size="small" class="mr-8" @click="openEditModal(record)">Edit</a-button>
										<a-popconfirm title="Delete this transaction?" okText="Yes" cancelText="No" @confirm="deleteTransaction(record.id)">
											<a-button size="small" type="danger">Delete</a-button>
										</a-popconfirm>
									</div>
								</template>
							</a-table-column>
						</a-table>
					</div>

					<!-- Mobile Card View -->
					<div class="mobile-cards">
						<a-spin :spinning="loading">
							<div v-for="record in transactions" :key="record.id" class="transaction-card">
								<a-card size="small" class="mb-3">
									<div class="transaction-row">
										<div class="transaction-main">
											<div class="transaction-amount" :class="record.type === 'EXPENSE' ? 'expense' : 'income'">
												{{ (record.type === 'EXPENSE' ? '-' : '+') + record.amount }}
											</div>
											<div class="transaction-details">
												<div class="transaction-category">{{ record.categoryName }}</div>
												<div class="transaction-date">{{ formatDisplayDate(record.date) }}</div>
											</div>
										</div>
										<div class="transaction-actions">
											<a-button v-if="record.receiptUrl" size="small" type="link" @click="downloadReceipt(record.id, record.receiptUrl)">
												<a-icon type="download" />
											</a-button>
											<a-button v-if="canEdit" size="small" @click="openEditModal(record)">
												<a-icon type="edit" />
											</a-button>
											<a-popconfirm v-if="canEdit" title="Delete?" okText="Yes" cancelText="No" @confirm="deleteTransaction(record.id)">
												<a-button size="small" type="danger">
													<a-icon type="delete" />
												</a-button>
											</a-popconfirm>
										</div>
									</div>
									<div v-if="record.notes" class="transaction-notes">
										{{ record.notes }}
									</div>
									<div class="transaction-meta">
										<span class="transaction-type">{{ record.type }}</span>
										<span v-if="record.createdBy" class="transaction-user">by {{ record.createdBy }}</span>
									</div>
								</a-card>
							</div>
						</a-spin>

						<!-- Mobile Pagination -->
						<div class="mobile-pagination" v-if="transactions.length > 0">
							<a-pagination 
								:current="pagination.current" 
								:total="pagination.total" 
								:pageSize="pagination.pageSize"
								:showSizeChanger="false"
								:showQuickJumper="false"
								:simple="true"
								@change="(page) => handleTableChange({ current: page, pageSize: pagination.pageSize })"
							/>
						</div>
					</div>
				</a-card>
			</a-col>
		</a-row>

		<!-- Create Modal -->
		<a-modal title="Add Transaction" :visible="showCreateModal" @ok="createTransaction" @cancel="closeCreateModal" :confirmLoading="creating">
			<a-form :form="createForm" layout="vertical">
				<a-form-item label="Type">
					<a-select v-decorator="['type', { rules: [{ required: true, message: 'Select type' }] }]" placeholder="Select type" @change="onTypeChange">
						<a-select-option value="INCOME">INCOME</a-select-option>
						<a-select-option value="EXPENSE">EXPENSE</a-select-option>
					</a-select>
				</a-form-item>
				<a-form-item label="Category">
					<a-select show-search optionFilterProp="children" v-decorator="['categoryId', { rules: [{ required: true, message: 'Select category' }] }]" placeholder="Select category" :disabled="!selectedType">
						<a-select-option v-for="c in categoriesByType" :key="c.id" :value="c.id">{{ c.name }}</a-select-option>
					</a-select>
				</a-form-item>
				<a-form-item label="Amount">
					<a-input-number style="width: 100%" :min="0" v-decorator="['amount', { rules: [{ required: true, message: 'Enter amount' }] }]" />
				</a-form-item>
				<a-form-item label="Date">
					<a-date-picker show-time style="width: 100%" v-decorator="['date', { rules: [{ required: true, message: 'Pick date' }], initialValue: currentDateTime }]" />
				</a-form-item>
				<a-form-item label="Notes">
					<a-textarea :rows="3" v-decorator="['notes']" placeholder="Optional notes about this transaction" />
				</a-form-item>
				<a-form-item label="Receipt (optional)">
					<a-upload :beforeUpload="beforeUploadCreate" :fileList="createReceiptList" @remove="onRemoveCreate">
						<a-button><a-icon type="upload" />Select File</a-button>
					</a-upload>
				</a-form-item>
			</a-form>
		</a-modal>

		<!-- Edit Modal -->
		<a-modal title="Edit Transaction" :visible="showEditModal" @ok="updateTransaction" @cancel="closeEditModal" :confirmLoading="updating">
			<a-form :form="editForm" layout="vertical">
				<a-form-item label="Type">
					<a-select v-decorator="['type', { rules: [{ required: true, message: 'Select type' }], initialValue: selected && selected.type }]" placeholder="Select type" @change="onEditTypeChange">
						<a-select-option value="INCOME">INCOME</a-select-option>
						<a-select-option value="EXPENSE">EXPENSE</a-select-option>
					</a-select>
				</a-form-item>
				<a-form-item label="Category">
					<a-select show-search optionFilterProp="children" v-decorator="['categoryId', { rules: [{ required: true, message: 'Select category' }], initialValue: selected && selected.categoryId }]" placeholder="Select category">
						<a-select-option v-for="c in editCategoriesByType" :key="c.id" :value="c.id">{{ c.name }}</a-select-option>
					</a-select>
				</a-form-item>
				<a-form-item label="Amount">
					<a-input-number style="width: 100%" :min="0" v-decorator="['amount', { rules: [{ required: true, message: 'Enter amount' }], initialValue: selected && selected.amount }]" />
				</a-form-item>
				<a-form-item label="Date">
					<a-date-picker show-time style="width: 100%" v-decorator="['date', { rules: [{ required: true, message: 'Pick date' }], initialValue: selectedDateForPicker }]" />
				</a-form-item>
				<a-form-item label="Notes">
					<a-textarea :rows="3" v-decorator="['notes', { initialValue: selected && selected.notes }]" />
				</a-form-item>
				<a-form-item label="Receipt (optional)">
					<a-upload :beforeUpload="beforeUploadEdit" :fileList="editReceiptList" @remove="onRemoveEdit">
						<a-button><a-icon type="upload" />Select File</a-button>
					</a-upload>
				</a-form-item>
			</a-form>
		</a-modal>

	</div>
</template>

<script>
	import { authFetch, OrganizationManager } from '../auth';

	export default {  // Remove the parenthesis
		data() {
			return {
				transactions: [],
				loading: false,
				pagination: { current: 1, pageSize: 10, total: 0 },
				filters: { type: undefined, categoryId: undefined, startDate: undefined, endDate: undefined, minAmount: undefined, maxAmount: undefined },
				categories: [],
				categoriesByType: [],
				editCategoriesByType: [],
				selectedType: null,
				filtersCollapsed: true,
				showCreateModal: false,
				showEditModal: false,
				creating: false,
				updating: false,
				selected: null,
				createReceiptFile: null,
				editReceiptFile: null,
				createReceiptList: [],
				editReceiptList: [],
			}
		},
		beforeCreate() {
			this.createForm = this.$form.createForm(this, { name: 'createTransaction' });
			this.editForm = this.$form.createForm(this, { name: 'editTransaction' });
		},
		mounted() {
			this.fetchCategories();
			this.fetchTransactions();
		},
		computed: {
			currentDateTime() {
				return this.$moment();
			},
			selectedDateForPicker() {
				return this.selected && this.selected.date ? this.$moment(this.selected.date) : undefined;
			},
			canEdit() {
				return OrganizationManager.canEdit();
			}
		},
		methods: {
			getRowKey(record) {
				return record.id;
			},
			formatDisplayDate(iso) {
				try { return this.$moment(iso).format('DD.MM.YYYY HH:mm'); } catch(e) { return iso; }
			},
			formatLocalDateTime(date) {
				const d = new Date(date);
				const pad = (n) => (n < 10 ? '0' + n : '' + n);
				return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
			},
			async fetchCategories() {
				try {
					const url = OrganizationManager.buildOrgUrl('/categories');
					const res = await authFetch(url);
					if (res.ok) {
						this.categories = await res.json();
					}
				} catch (e) {
					console.error('fetchCategories error', e);
					this.$notification.error({
						message: 'Error',
						description: 'Failed to load categories'
					});
				}
			},
			async fetchCategoriesByType(type) {
				try {
					const url = OrganizationManager.buildOrgUrl(`/categories/type/${type}`);
					const res = await authFetch(url);
					if (res.ok) {
						this.categoriesByType = await res.json();
					} else {
						this.categoriesByType = [];
					}
				} catch (e) {
					console.error('fetchCategoriesByType error', e);
					this.categoriesByType = [];
				}
			},
			onTypeChange(type) {
				this.selectedType = type;
				if (type) {
					this.fetchCategoriesByType(type);
					// Clear the category selection when type changes
					this.createForm.setFieldsValue({ categoryId: undefined });
				} else {
					this.categoriesByType = [];
				}
			},
			async onEditTypeChange(type) {
				if (type) {
					await this.fetchEditCategoriesByType(type);
					
					// If editing existing transaction, try to keep the same categoryId if it exists in the new type
					const currentCategoryId = this.selected?.categoryId;
					const categoryExists = currentCategoryId && this.editCategoriesByType.find(c => c.id === currentCategoryId);
					
					this.editForm.setFieldsValue({ 
						categoryId: categoryExists ? currentCategoryId : undefined 
					});
				} else {
					this.editCategoriesByType = [];
					this.editForm.setFieldsValue({ categoryId: undefined });
				}
			},
			async fetchEditCategoriesByType(type) {
				try {
					const url = OrganizationManager.buildOrgUrl(`/categories/type/${type}`);
					const res = await authFetch(url);
					if (res.ok) {
						this.editCategoriesByType = await res.json();
					} else {
						this.editCategoriesByType = [];
					}
				} catch (e) {
					console.error('fetchEditCategoriesByType error', e);
					this.editCategoriesByType = [];
				}
			},
			async fetchTransactions(page = this.pagination.current, pageSize = this.pagination.pageSize) {
				this.loading = true;
				try {
					const baseEndpoint = this.hasAnyFilter() ? '/transactions/search' : '/transactions';
					const baseUrl = OrganizationManager.buildOrgUrl(baseEndpoint);
					const url = new URL(baseUrl);
					url.searchParams.append('page', page - 1);
					url.searchParams.append('size', pageSize);
          url.searchParams.append('sort', 'date,desc');
					if (this.hasAnyFilter()) {
						this.appendFilters(url);
					}
					const res = await authFetch(url.toString());
					if (res.ok) {
						const data = await res.json();
						// Support both paginated or array response
						const content = Array.isArray(data) ? data : (data.content || []);
						this.transactions = content;
						this.pagination = {
							current: page,
							pageSize,
							total: Array.isArray(data) ? content.length : (data.totalElements || content.length)
						};
					} else {
						this.$message.error('Failed to load transactions');
					}
				} catch (e) {
					console.error('fetchTransactions error', e);
					this.$message.error('Error loading transactions');
				} finally{
					this.loading = false;
				}
			},
			hasAnyFilter() {
				const f = this.filters;
				return !!(f.type || f.categoryId || f.startDate || f.endDate || f.minAmount || f.maxAmount);
			},
			appendFilters(url) {
				const f = this.filters;
				if (f.type) url.searchParams.append('type', f.type);
				if (f.categoryId) url.searchParams.append('categoryId', f.categoryId);
				if (f.startDate) url.searchParams.append('startDate', this.formatLocalDateTime(f.startDate));
				if (f.endDate) url.searchParams.append('endDate', this.formatLocalDateTime(f.endDate));
				if (typeof f.minAmount === 'number') url.searchParams.append('minAmount', f.minAmount);
				if (typeof f.maxAmount === 'number') url.searchParams.append('maxAmount', f.maxAmount);
			},
			applyFilters() {
				this.pagination.current = 1;
				this.fetchTransactions(1, this.pagination.pageSize);
			},
			resetFilters() {
				this.filters = { type: undefined, categoryId: undefined, startDate: undefined, endDate: undefined, minAmount: undefined, maxAmount: undefined };
				this.pagination.current = 1;
				this.fetchTransactions(1, this.pagination.pageSize);
			},
			handleTableChange(pagination) {
				this.pagination.current = pagination.current;
				this.pagination.pageSize = pagination.pageSize;
				this.fetchTransactions(pagination.current, pagination.pageSize);
			},
			openCreateModal() {
				this.showCreateModal = true;
				this.createForm.resetFields();
				this.createReceiptList = [];
				this.createReceiptFile = null;
				this.selectedType = null;
				this.categoriesByType = [];
			},
			closeCreateModal() {
				this.showCreateModal = false;
			},
			beforeUploadCreate(file) {
				this.createReceiptFile = file;
				this.createReceiptList = [file];
				return false;
			},
			onRemoveCreate() {
				this.createReceiptFile = null;
				this.createReceiptList = [];
			},
			async createTransaction() {
				this.createForm.validateFields(async (err, values) => {
					if (!err) {
						this.creating = true;
						try {
							const payload = {
								amount: values.amount,
								date: this.formatLocalDateTime(values.date),
								categoryId: values.categoryId,
								notes: values.notes || '',
								type: values.type,
							};
							const formData = new FormData();
							formData.append('transaction', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
							if (this.createReceiptFile) {
								formData.append('receipt', this.createReceiptFile);
							}
							const url = OrganizationManager.buildOrgUrl('/transactions');
							const token = localStorage.getItem('token');
							const res = await fetch(url, {
								method: 'POST',
								headers: { 'Authorization': `Bearer ${token}` },
								body: formData,
							});
							if (res.ok) {
								this.$message.success('Transaction created');
								this.showCreateModal = false;
								this.fetchTransactions();
							} else {
								this.$message.error('Failed to create transaction');
							}
						} catch (e) {
							console.error('createTransaction error', e);
							this.$message.error('Error creating transaction');
						} finally {
							this.creating = false;
						}
					}
				});
			},
			async openEditModal(record) {
				this.selected = Object.assign({}, record);
				this.showEditModal = true;
				this.editReceiptFile = null;
				this.editReceiptList = [];

				// Load categories for the current transaction type
				if (record.type) {
					await this.fetchEditCategoriesByType(record.type);
				}

				this.$nextTick(() => {
					this.editForm.setFieldsValue({
						type: record.type,
						amount: record.amount,
						date: this.$moment(record.date),
						categoryId: record.categoryId,
						notes: record.notes,
					});
				});
			},
			closeEditModal() {
				this.showEditModal = false;
			},
			beforeUploadEdit(file) {
				this.editReceiptFile = file;
				this.editReceiptList = [file];
				return false;
			},
			onRemoveEdit() {
				this.editReceiptFile = null;
				this.editReceiptList = [];
			},
			async updateTransaction() {
				this.editForm.validateFields(async (err, values) => {
					if (!err && this.selected) {
						this.updating = true;
						try {
							const payload = {
								amount: values.amount,
								date: this.formatLocalDateTime(values.date),
								categoryId: values.categoryId,
								notes: values.notes || '',
								type: values.type,
							};
							const formData = new FormData();
							formData.append('transaction', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
							if (this.editReceiptFile) {
								formData.append('receipt', this.editReceiptFile);
							}
							const url = OrganizationManager.buildOrgUrl(`/transactions/${this.selected.id}`);
							const token = localStorage.getItem('token');
							const res = await fetch(url, {
								method: 'PUT',
								headers: { 'Authorization': `Bearer ${token}` },
								body: formData,
							});
							if (res.ok) {
								this.$message.success('Transaction updated');
								this.showEditModal = false;
								this.fetchTransactions();
							} else {
								this.$message.error('Failed to update transaction');
							}
						} catch (e) {
							console.error('updateTransaction error', e);
							this.$message.error('Error updating transaction');
						} finally {
							this.updating = false;
						}
					}
				});
			},
			async deleteTransaction(id) {
				try {
					const url = OrganizationManager.buildOrgUrl(`/transactions/${id}`);
					const res = await authFetch(url, {
						method: 'DELETE',
					});
					if (res.ok) {
						this.$message.success('Transaction deleted');
						this.fetchTransactions();
					} else {
						this.$message.error('Failed to delete transaction');
					}
				} catch (e) {
					console.error('deleteTransaction error', e);
					this.$message.error('Error deleting transaction');
				}
			},
			async downloadReceipt(transactionId, receiptUrl) {
				try {
					const url = OrganizationManager.buildOrgUrl(`/transactions/receipt/${transactionId}`);
					const response = await authFetch(url);

					if (response.ok) {
						const blob = await response.blob();
						const downloadUrl = window.URL.createObjectURL(blob);
						const a = document.createElement('a');
						a.href = downloadUrl;
						a.download = `receipt-${transactionId}.pdf`;
						document.body.appendChild(a);
						a.click();
						window.URL.revokeObjectURL(downloadUrl);
						document.body.removeChild(a);
					} else {
						this.$message.error('Failed to download receipt');
					}
				} catch (e) {
					console.error('downloadReceipt error', e);
					this.$message.error('Error downloading receipt');
				}
			},
		},
		name: 'Transactions',
	}
</script>

<style lang="scss">
/* Desktop view - show table, hide cards */
.desktop-table {
  display: block;
}

.mobile-cards {
  display: none;
}

/* Mobile view - hide table, show cards */
@media (max-width: 768px) {
  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }

  .transaction-card {
    .transaction-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;
    }

    .transaction-main {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .transaction-amount {
      font-size: 16px;
      font-weight: 600;
      min-width: 80px;

      &.expense {
        color: #ff4d4f;
      }

      &.income {
        color: #52c41a;
      }
    }

    .transaction-details {
      flex: 1;
    }

    .transaction-category {
      font-weight: 500;
      color: #262626;
      margin-bottom: 2px;
    }

    .transaction-date {
      font-size: 12px;
      color: #8c8c8c;
    }

    .transaction-actions {
      display: flex;
      gap: 4px;
      flex-shrink: 0;
    }

    .transaction-notes {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #f0f0f0;
      font-size: 13px;
      color: #595959;
      font-style: italic;
    }

    .transaction-meta {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #f0f0f0;
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      color: #8c8c8c;
    }

    .transaction-type {
      background: #f0f0f0;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 11px;
      text-transform: uppercase;
    }
  }

  .mobile-pagination {
    text-align: center;
    margin-top: 16px;
  }
}

/* Tablet view adjustments */
@media (max-width: 1024px) and (min-width: 769px) {
  .ant-table {
    font-size: 13px;
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 6px;
    }
  }
}
</style>